import { useFileDownload } from '@/hooks/useFileDownload'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Button,
  DownloadIcon,
  Flex,
  Grid,
  Text,
  tokenPrefix,
} from '@hivewatch/dark'

import { IncidentQuery } from '@/graphql/generated/operations'
import { EmailAuthVerdict } from '@/graphql/generated/schemas'
import {
  formatDateTime,
  formatDateTimeInTimeZone,
} from '@/utils/formatDateTime'
import { tVar } from '@/utils/i18n'

export const ORIGINAL_EMAIL_FILE_NAME = 'original-message.eml'

type IncidentEmailT = NonNullable<IncidentQuery['incident']['email']>

interface OriginalEmailIProps {
  /** Null when the email didn't resolve for an Email Incident. */
  email: IncidentEmailT | null
  facilityTimeZone?: string | null
}

/**
 * The email an Email Incident was created from (RFC0197). Everything is plain
 * text: the body is never rendered as HTML and nothing in it becomes a link.
 * `from` is whatever the email claimed, so it is never shown as verified;
 * `authSummary` (SES's SPF, DKIM and DMARC verdicts) is the only
 * authentication result shown.
 */
export const OriginalEmail = ({
  email,
  facilityTimeZone,
}: OriginalEmailIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = tokenPrefix(colorScheme)
  const { handleDownload, isDownloading } = useFileDownload()
  const downloadUrl = email?.downloadUrl

  return (
    <Flex
      data-testid='originalEmail'
      direction='column'
      gap='8px'
      mb='24px'
      mt='24px'
      w='full'
    >
      <Flex alignItems='center' gap='12px' justifyContent='space-between'>
        <Text color={`${prefix}text.secondary`} textStyle='overline'>
          {t('incidents:original_email_title')}
        </Text>
        {downloadUrl && (
          <Button
            colorScheme={colorScheme}
            isLoading={isDownloading}
            leftIcon={<DownloadIcon colorScheme={colorScheme} size={16} />}
            onClick={() =>
              void handleDownload(downloadUrl, ORIGINAL_EMAIL_FILE_NAME)
            }
            size='sm'
            variant='secondary'
          >
            {ORIGINAL_EMAIL_FILE_NAME}
          </Button>
        )}
      </Flex>
      {email ? (
        <OriginalEmailContent
          email={email}
          facilityTimeZone={facilityTimeZone}
          onDownload={() => {
            if (downloadUrl) {
              void handleDownload(downloadUrl, ORIGINAL_EMAIL_FILE_NAME)
            }
          }}
        />
      ) : (
        <Alert
          colorScheme={colorScheme}
          title={t('incidents:original_email_unavailable')}
          variant='info'
        />
      )}
    </Flex>
  )
}

interface OriginalEmailContentIProps {
  email: IncidentEmailT
  facilityTimeZone?: string | null
  onDownload: () => void
}

const OriginalEmailContent = ({
  email,
  facilityTimeZone,
  onDownload,
}: OriginalEmailContentIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = tokenPrefix(colorScheme)

  const facilityLocalReceivedAt = formatDateTimeInTimeZone(
    email.receivedAt,
    facilityTimeZone
  )
  const receivedAt = `${formatDateTime(email.receivedAt)}${
    facilityLocalReceivedAt
      ? ` (${facilityLocalReceivedAt} ${t(
          'incidents:incident_drawer_facility_local_time'
        )})`
      : ''
  }`

  const verdictLabels: Record<EmailAuthVerdict, string> = {
    [EmailAuthVerdict.Disabled]: t('incidents:original_email_auth_disabled'),
    [EmailAuthVerdict.Fail]: t('incidents:original_email_auth_fail'),
    [EmailAuthVerdict.Gray]: t('incidents:original_email_auth_gray'),
    [EmailAuthVerdict.Pass]: t('incidents:original_email_auth_pass'),
    [EmailAuthVerdict.ProcessingFailed]: t(
      'incidents:original_email_auth_processing_failed'
    ),
    [EmailAuthVerdict.Unknown]: t('incidents:original_email_auth_unknown'),
  }
  // A verdict this build doesn't know reads as unknown.
  const verdict = (value: EmailAuthVerdict) =>
    verdictLabels[value] ?? verdictLabels[EmailAuthVerdict.Unknown]
  const { dkim, dmarc, spf } = email.authSummary
  const authSummary = tVar(t, 'incidents:original_email_auth_summary', {
    dkim: verdict(dkim),
    dmarc: verdict(dmarc),
    spf: verdict(spf),
  })

  const rows = [
    { label: t('incidents:original_email_from'), value: email.from },
    { label: t('incidents:original_email_subject'), value: email.subject },
    { label: t('incidents:original_email_received'), value: receivedAt },
    {
      label: t('incidents:original_email_authentication'),
      value: authSummary,
    },
  ]

  // No preserved copy (the copy failed, or the email was INFECTED): the stored
  // body isn't shown either. A null body means parsing failed or timed out.
  const isUnavailable = !email.downloadUrl
  const body = isUnavailable ? null : email.bodyText

  return (
    <>
      <Box
        bg={`${prefix}bg.card`}
        borderColor={`${prefix}border.default`}
        borderWidth='1px'
        p='12px'
      >
        <Grid columnGap='12px' rowGap='4px' templateColumns='max-content 1fr'>
          {rows.map(({ label, value }) => (
            <Box display='contents' key={label}>
              <Text color={`${prefix}text.secondary`} textStyle='caption'>
                {label}
              </Text>
              <Text
                color={`${prefix}text.primary`}
                overflowWrap='anywhere'
                textStyle='caption'
              >
                {value}
              </Text>
            </Box>
          ))}
        </Grid>
        {body != null && (
          <Text
            color={`${prefix}text.primary`}
            data-testid='originalEmail_body'
            mt='12px'
            overflowWrap='anywhere'
            textStyle='caption'
            whiteSpace='pre-wrap'
          >
            {body}
          </Text>
        )}
        {/* Under the headers, as in the design. The body is already text
            (the backend converts HTML), so this only means parsing failed. */}
        {!isUnavailable && body == null && (
          <Box mt='12px'>
            <Alert
              colorScheme={colorScheme}
              description={t('incidents:original_email_body_unavailable_body')}
              primaryButton={{
                onClick: onDownload,
                text: t('incidents:original_email_download'),
              }}
              title={t('incidents:original_email_body_unavailable_title')}
              variant='info'
            />
          </Box>
        )}
      </Box>
      {body != null && email.bodyTruncated && (
        <Text color={`${prefix}text.secondary`} textStyle='caption'>
          {t('incidents:original_email_truncated')}
        </Text>
      )}
      {isUnavailable && (
        <Alert
          colorScheme={colorScheme}
          title={t('incidents:original_email_unavailable')}
          variant='info'
        />
      )}
    </>
  )
}
